// ── Cart item identity ──────────────────────────────────────────────────
// Product IDs and Combo IDs are independent sequences (both start at 1), so
// a plain numeric id is NOT safe to use as a cart key — a combo #1 and a
// product #1 would collide into the same cart row.
//
// Every cart item therefore carries:
//   id       – namespaced, unique cart key ("product-12" / "combo-3")
//   refId    – the real DB id, used for navigation / API calls
//   itemType – "product" | "combo"
//
// ALL pages that read or write the cart must build/read items through these
// helpers so the key scheme never drifts out of sync between pages again.

export const productCartKey = (id) => `product-${id}`;
export const comboCartKey = (id) => `combo-${id}`;

export const toProductCartItem = (product) => ({
  ...product,
  id: productCartKey(product.id),
  refId: product.id,
  itemType: "product",
  variant_label: product.quantity_unit
    ? (product.quantity_unit.toLowerCase() === "size"
      ? product.quantity
      : `${parseFloat(product.quantity)} ${product.quantity_unit}`)
    : null,
});

export const toComboCartItem = (combo) => ({
  id: comboCartKey(combo.id),
  refId: combo.id,
  itemType: "combo",
  product_name: combo.combo_name,
  product_image: combo.combo_image,
  price: combo.combo_price,
  offer_price: null,
  original_price: combo.original_price,
});

// Read a cart qty for a given raw product/combo id.
export const getCartQtyFor = (cartState, type, id) => {
  const key = type === "combo" ? comboCartKey(id) : productCartKey(id);
  const item = cartState.find((p) => p.id === key);
  return item ? item.qty : 0;
};

// Cart item -> {product|combo, quantity} line the backend expects
// (checkout-initiate, checkout-validate, orders/reserve, courier/calculate,
// order_data.items).
export const toOrderLine = (item) =>
  item.itemType === "combo"
    ? { combo: item.refId, quantity: item.qty }
    : { product: item.refId, quantity: item.qty };

// Backend `reserved_items` / `valid_items` line -> cart item shape.
export const mapReservedToCartItems = (items = []) =>
  items.map((i) => ({
    id: i.type === "combo" ? comboCartKey(i.id) : productCartKey(i.id),
    refId: i.id,
    itemType: i.type,
    qty: i.qty,
    product_name: i.product_name,
    price: Number(i.price),
    offer_price: i.offer_price !== null && i.offer_price !== undefined ? Number(i.offer_price) : null,
    original_price: i.original_price !== null && i.original_price !== undefined ? Number(i.original_price) : null,
    product_image: i.product_image,
  }));